import { FC, useContext } from 'react';
import { ActionIcon, Group, Loader, ThemeIcon, Tooltip } from '@mantine/core';
import {
  IconClock,
  IconPlayerPlay,
  IconPlayerSkipForward,
  IconPlayerTrackNext,
  IconRefreshAlert,
  IconUserCheck,
} from '@tabler/icons';
import { WorkflowDTO, WorkflowOperation, WorkflowStepDTO } from '../../Types/LogT';
import { EventContext } from '../../Service/Context/EventContext';
import { handleWorkflowForceContinue } from '../../Molecules/Event/Workflows/forceContinue';

const StepExecutionStatusIcon: FC<{
  step: WorkflowStepDTO;
  workflow: WorkflowDTO;
  currentStep: boolean;
  runOperation: (operation: WorkflowOperation) => void;
  allowedOperations: WorkflowOperation[];
}> = ({ step, workflow, currentStep, runOperation, allowedOperations }) => {
  const { executionReport, procedureType } = step;
  const { event } = useContext<any>(EventContext);
  const { executionStatus } = executionReport ?? {};
  const { name: eventName } = event ?? {};

  if (!currentStep) {
    return null;
  }

  if (procedureType === 'WAIT_UNTIL' && allowedOperations.includes('FORCE_CONTINUE')) {
    return (
      <Tooltip label="Manuell akzeptieren">
        <ActionIcon size="sm" radius="xl" onClick={() => handleWorkflowForceContinue(workflow, runOperation)}>
          <IconPlayerTrackNext color="#FEC170" size={14} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (allowedOperations.includes('START')) {
    return (
      <Tooltip label="Workflow starten" className={eventName === 'Onboarding-Event' ? 'highlight' : undefined}>
        <ActionIcon size="sm" radius="xl" onClick={() => runOperation('START')} sx={{ cursor: 'pointer' }}>
          <IconPlayerPlay color="#4EBF46" size={14} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (allowedOperations.includes('RETRY')) {
    return (
      <Tooltip label="Noch mal versuchen">
        <ActionIcon size="sm" radius="xl" onClick={() => runOperation('RETRY')} sx={{ cursor: 'pointer' }}>
          <IconRefreshAlert color="red" size={14} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (allowedOperations.includes('APPROVE')) {
    return (
      <Group spacing={4} noWrap>
        <Tooltip label="Schritt genehmigen">
          <ActionIcon
            size="sm"
            color="orange"
            radius="xl"
            onClick={() => runOperation('APPROVE')}
            sx={{ cursor: 'pointer' }}
          >
            <IconUserCheck size={14} />
          </ActionIcon>
        </Tooltip>
        {allowedOperations.includes('SKIP') && (
          <Tooltip label="Schritt überspringen">
            <ActionIcon
              size="sm"
              color="orange"
              radius="xl"
              onClick={() => runOperation('SKIP')}
              sx={{ cursor: 'pointer' }}
            >
              <IconPlayerSkipForward size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    );
  }

  if (allowedOperations.includes('SKIP'))
    return (
      <Tooltip label="Schritt überspringen">
        <ActionIcon
          size="sm"
          color="orange"
          radius="xl"
          onClick={() => runOperation('SKIP')}
          sx={{ cursor: 'pointer' }}
        >
          <IconPlayerSkipForward size={14} />
        </ActionIcon>
      </Tooltip>
    );

  if (allowedOperations.includes('CONTINUE')) {
    return (
      <Tooltip label="Workflow fortzuführen">
        <ActionIcon size="sm" radius="xl" onClick={() => runOperation('CONTINUE')}>
          <IconPlayerSkipForward size={14} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (executionStatus === 'SCHEDULED') {
    return (
      <ThemeIcon size="sm" radius="xl" color="orange">
        <IconClock size={14} />
      </ThemeIcon>
    );
  }

  if (executionStatus !== 'ABORTED') {
    return <Loader variant="dots" size="sm" />;
  }
  return null;
};

export default StepExecutionStatusIcon;
